// Component: UpsertService
// Description: This component is used view (create/update) service screen.
import * as React from 'react';
import Page from "components/UI/Page";
import ServiceFormHeader from "./ServiceFormHeader";
import UpsertServiceForm from "./UpsertServiceForm";
import Vendor from "data/Vendor";
import { useServiceForm } from 'hooks/useServiceForm';
import { Service, ServiceProducts, ServiceStatus, VariantSKUForm } from 'data/Service';
import { useAPI } from 'lib/API';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { deleteServices, getService } from 'requests/services';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { getProductsBySKUs } from 'requests/product';
import { SERVICE_CONSTANTS, SERVICE_FORM_FIELDS, ADDON_STATUS_CODE } from 'constants/service';
import { COUNTRY_ISO } from 'constants/country';
import { navigate } from 'hooks/useHookRouterCompat';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import { getItems } from 'lib/SessionStorage';
import ConfirmationModal from 'components/UI/ConfimationModal';
import { useModalState, ModalType } from 'hooks/useAddonModalState';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';

interface Props {
    serviceId?: string;
    selectedVendor: Vendor;
    vendorsList?: Vendor[];
    updateVendor?: (name: string) => void;
    selectedBusinessUnit?: BusinessUnit;
    updateBusinessUnit?: (id: number) => void;
    featureFlags?: FeatureFlag;
}

const ServiceDetails: React.FC<Props> = ({ serviceId, selectedVendor, updateBusinessUnit, selectedBusinessUnit, featureFlags }: Props) => {

    const [service, setService] = React.useState<Service>();
    const {
        control,
        handleSubmit,
        watch,
        errors,
        isValid,
        onSubmit,
        setValue,
        getValues,
        isLoading,
        setError,
        clearErrors,
        trigger,
        isDirty
    } = useServiceForm(service);  // Using the custom hook

    const formState = watch();
    const { modalState, showModal, hideModal } = useModalState([
        'delete', 'unsavedChanges', 'publish', 'unpublish'
    ]);

    const [searchTerm, setSearchTerm] = React.useState<string>();
    const [invalidSkus, setInvalidSkus] = React.useState<string>();
    const [existingSkus, setExistingSkus] = React.useState<string>();
    const [variantForm, setVariantForm] = React.useState<VariantSKUForm>({ quantity: 1, isOptional: false });

    const serviceStatuses: ServiceStatus[] = getItems(STORAGE_KEYS.SERVICE_STATUSES);

    const serviceDetailReq = useAPI({
        deferFn: getService,
        onResolve: (result) => {
            setService(result.data);
        },
        onReject: (error) => {
            renderToast(toast.TYPE.ERROR, error.message);
        }
    });


    const searchSkusReq = useAPI({
        deferFn: getProductsBySKUs,
        onResolve: (data: ServiceProducts) => {
            const invalidSkus = data.invalidSkus;
            const stringifiedSkus = invalidSkus?.join(',');
            const stringifiedSkusInput = invalidSkus?.join(', ');
            setInvalidSkus(stringifiedSkusInput);
            setSearchTerm(stringifiedSkus);

            let variants = data.productVariants;

            /**
             * Updates the variant properties when a SKU is found:
             * - Sets the 'quantity' value from the input field to all newly added variants
             * - Applies the 'isOptional' flag based on the checkbox selection
             * - Resets the isOptional and quantity input values after adding to maintain clean state
             */

            if (variants.length > 0) {
                // Apply quantity and isOptional to all new variants
                variants = variants.map(variant => ({
                    ...variant,
                    quantity: variantForm.quantity,
                    isOptional: variantForm.isOptional
                }));

                // Reset inputs after adding
                setVariantForm({ quantity: 1, isOptional: false });
                setValue(SERVICE_FORM_FIELDS.OPTIONAL_SKU, false)

            }
            const currentVariants = getValues(SERVICE_FORM_FIELDS.VARIANTS);

            setValue(SERVICE_FORM_FIELDS.VARIANTS, [...variants, ...currentVariants]);

            if (stringifiedSkusInput) {
                renderToast(toast.TYPE.WARNING, `${SERVICE_CONSTANTS.SKUS_NOT_FOUND}: ${stringifiedSkusInput}`);
            }
        },
        onReject: err => {
            renderToast(toast.TYPE.ERROR, err.message);
        }
    });

    const deleteReq = useAPI({
        deferFn: deleteServices,
        onResolve: () => {
            renderToast(toast.TYPE.SUCCESS, SERVICE_CONSTANTS.SERVICE_DELETED);
            hideModal('delete');
            navigate('/addons');
        },
        onReject: (err: any) => {
            renderToast(toast.TYPE.ERROR, err.message);
            hideModal('delete');
        }
    });

    // Fetch service details if serviceId is provided
    React.useEffect(() => {
        if (serviceId) {
            // Fetch service details
            serviceDetailReq.run(serviceId);
        }
    }, [serviceId]);

    React.useEffect(() => {
        if (service?.businessUnitId) {
            updateBusinessUnit?.(service.businessUnitId!);
        }
    }, [service]);

    React.useEffect(() => {
        if (service) {
            // For each service price, set the appropriate country and UOM values
            service.servicePrices.forEach(price => {
                const isCanada = price.country?.countryIso === COUNTRY_ISO.CA;
                const isUSA = price.country?.countryIso === COUNTRY_ISO.US;

                if (isCanada) {
                    setValue(SERVICE_FORM_FIELDS.IS_FOR_CANADA, true);
                    setValue(SERVICE_FORM_FIELDS.CANADA_PRICE, price.pricePerUnit.toString());
                    // Important: Set the UOM from service data
                    if (price.unitOfMeasurementId) {
                        setValue(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_CA, price.unitOfMeasurementId);
                    }
                }

                if (isUSA) {
                    setValue(SERVICE_FORM_FIELDS.IS_FOR_USA, true);
                    setValue(SERVICE_FORM_FIELDS.USA_PRICE, price.pricePerUnit.toString());
                    // Important: Set the UOM from service data
                    if (price.unitOfMeasurementId) {
                        setValue(SERVICE_FORM_FIELDS.MEASUREMENT_UNIT_USA, price.unitOfMeasurementId);
                    }
                }
            });
        }
    }, [service, setValue]);

    const onSave = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();

        if (service?.serviceStatus?.code === ADDON_STATUS_CODE.PUBLISHED && !formState?.productVariants?.length) {
            renderToast(toast.TYPE.ERROR, SERVICE_CONSTANTS.PUBLISH_SERVICE_WARNING);
            return;
        }

        const draftStatusId = serviceStatuses.find(status => status.code === ADDON_STATUS_CODE.DRAFT)?.id;
        const isFormValid = await trigger(); // Validate all fields
        if (!isFormValid) return;

        handleSubmit((data) => onSubmit({ ...data, vendorId: selectedVendor.id, businessUnitId: selectedBusinessUnit?.id, statusId: service?.serviceStatus?.id || draftStatusId }))();
    }

    const onPublish = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();

        if (!formState?.productVariants?.length) {
            renderToast(toast.TYPE.ERROR, SERVICE_CONSTANTS.PUBLISH_SERVICE_WARNING);
            return;
        }

        // Validate form before showing confirmation
        trigger().then(isValid => {
            if (isValid) {
                showModal('publish');
            }
        });
    };

    const onUnPublish = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        showModal('unpublish');
    };

    const handleConfirmPublish = () => {
        const publishedStatusId = serviceStatuses.find(status => status.code === ADDON_STATUS_CODE.PUBLISHED)?.id;
        handleSubmit((data) => onSubmit({ ...data, vendorId: selectedVendor.id, businessUnitId: selectedBusinessUnit?.id, statusId: publishedStatusId }))();
        hideModal('publish');
    };

    const handleConfirmUnPublish = () => {
        const unpublishedStatusId = serviceStatuses.find(status => status.code === ADDON_STATUS_CODE.UNPUBLISHED)?.id;
        handleSubmit((data) => onSubmit({ ...data, vendorId: selectedVendor.id, businessUnitId: selectedBusinessUnit?.id, statusId: unpublishedStatusId }))();
        hideModal('unpublish');
    };

    const onDelete = () => {
        if (service?.id) {
            const ids = [service.id];
            deleteReq.run(ids);
        }
    }

    const onCancel = () => {
        if (isDirty) {
            // Show confirmation modal if there are unsaved changes
            showModal('unsavedChanges');
        } else {
            // Navigate directly if no changes
            navigate('/addons');
        }
    };

    const handleDelete = () => {
        showModal('delete');
    }

    const handleSearch = () => {
        if (!searchTerm) return;

        if (!selectedVendor?.id) {
            renderToast(toast.TYPE.INFO, SERVICE_CONSTANTS.PLEASE_SELECT_VENDOR);
            return;
        }

        const variants = getValues(SERVICE_FORM_FIELDS.VARIANTS);
        let skus = searchTerm?.split(',')?.map(sku => sku?.trim());
        const existingSkus = variants.map(x => x.sku?.toLowerCase()?.trim());
        const newSkus = skus.filter(sku => !existingSkus.includes(sku?.toLowerCase()));
        const alreadyAddedSkus = skus.filter(sku => existingSkus.includes(sku?.toLowerCase()));

        if (alreadyAddedSkus.length > 0) {
            const stringifiedSkus = alreadyAddedSkus.join(', ');
            setExistingSkus(stringifiedSkus);
        }

        if (!selectedBusinessUnit?.id && featureFlags?.businessUnit?.enabled) {
            renderToast(toast.TYPE.INFO, SERVICE_CONSTANTS.PLEASE_SELECT_BUSINESS_UNIT);
            return;
        }

        if (newSkus.length > 0) searchSkusReq.run(newSkus, selectedVendor?.id, selectedBusinessUnit?.id)
        else setSearchTerm('');
    }

    const handleConfirmNavigation = () => {
        hideModal('unsavedChanges');
        navigate('/addons');
    };

    const isLoadingSku = searchSkusReq.isLoading;
    const serviceStatus = service?.serviceStatus?.code;

    const modalConfigs = {
        delete: {
            title: `${GENERAL_CONSTANTS.REMOVE} ${SERVICE_CONSTANTS.SERVICE}`,
            message: `${SERVICE_CONSTANTS.ARE_YOU_SURE} ${GENERAL_CONSTANTS.THIS} ${SERVICE_CONSTANTS.SERVICE}?`,
            onConfirm: onDelete,
            cancelText: GENERAL_CONSTANTS.BACK
        },
        unsavedChanges: {
            title: GENERAL_CONSTANTS.UNSAVED_CHANGES,
            message: SERVICE_CONSTANTS.UNSAVE_CHANGES_DESC,
            onConfirm: handleConfirmNavigation
        },
        publish: {
            title: SERVICE_CONSTANTS.PUBLISH_SERVICE,
            message: SERVICE_CONSTANTS.PUBLISH_CONFIRMATION,
            onConfirm: handleConfirmPublish
        },
        unpublish: {
            title: SERVICE_CONSTANTS.UNPUBLISH_SERVICE,
            message: SERVICE_CONSTANTS.UNPUBLISH_CONFIRMATION,
            onConfirm: handleConfirmUnPublish
        }
    };

    if (serviceDetailReq.isLoading || isLoading || deleteReq.isLoading) return <LoadingIndicator center />


    return (
        <Page>
            <>
                {Object.entries(modalConfigs).map(([key, config]) => (
                    <ConfirmationModal
                        key={key}
                        show={modalState[key as ModalType]}
                        onClose={() => hideModal(key as ModalType)}
                        onConfirm={config.onConfirm}
                        title={config.title}
                        message={config.message}
                        confirmText={GENERAL_CONSTANTS.CONFIRM}
                        cancelText={GENERAL_CONSTANTS.CANCEL}
                    />
                ))}

                <ServiceFormHeader
                    onSave={onSave}
                    onPublish={onPublish}
                    onUnPublish={onUnPublish}
                    onDelete={handleDelete}
                    onCancel={onCancel}
                    isValid={isValid && !isLoadingSku}
                    serviceStatus={serviceStatus}
                />

                <UpsertServiceForm
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                    watch={watch}
                    searchTerm={searchTerm!}
                    setSearchTerm={setSearchTerm}
                    variantForm={variantForm}
                    setVariantForm={setVariantForm}
                    onSearch={handleSearch}
                    invalidSkus={invalidSkus!}
                    existingSkus={existingSkus!}
                    setExistingSkus={setExistingSkus}
                    setInvalidSkus={setInvalidSkus}
                    isLoading={isLoadingSku}
                    setError={setError}
                    clearErrors={clearErrors}
                    vendorId={selectedVendor?.id!}
                    businessUnitId={selectedBusinessUnit?.id!}
                />
            </>
        </Page>
    );
};

export default ServiceDetails;

